import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  rolesLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isBroker: boolean;
  canAccessBrokerage: boolean;
  /** Lead categories (verksamheter) the signed-in user may work with. */
  allowedCategories: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Verksamheter (lead categories). Kept local to avoid a component import cycle.
export const ALL_LEAD_CATEGORIES = [
  'soker_redovisningsbyra',
  'soker_revisor',
  'kopare_av_byra',
  'saljare_av_byra',
  'nyfikenmodellen',
] as const;

// Categories that always require brokerage access
export const BROKERAGE_CATEGORIES: string[] = ['kopare_av_byra', 'saljare_av_byra'];


export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBroker, setIsBroker] = useState(false);
  const [canAccessBrokerage, setCanAccessBrokerage] = useState(false);
  const [allowedCategories, setAllowedCategories] = useState<string[]>([]);
  const navigate = useNavigate();

  const checkRoles = async (userId: string) => {
    setRolesLoading(true);
    await Promise.all([
      checkAdminRole(userId),
      checkBrokerRole(userId),
      checkBrokeragePermission(userId),
      checkCategories(userId)
    ]);
    setRolesLoading(false);
  };

  // Which verksamheter (lead categories) the user may work with.
  // Mirrors private.lead_category_access in the database.
  const checkCategories = async (userId: string) => {
    try {
      const [{ data: admin }, { data: brokerage }, { data: perms }] = await Promise.all([
        supabase.rpc('has_role', { _user_id: userId, _role: 'admin' }),
        supabase.rpc('can_access_brokerage'),
        supabase.from('user_permissions').select('permission_key, enabled').eq('user_id', userId),
      ]);

      if (admin) {
        setAllowedCategories([...ALL_LEAD_CATEGORIES]);
        return;
      }

      const enabledCats = (perms || [])
        .filter(p => p.enabled && p.permission_key.startsWith('cat_'))
        .map(p => p.permission_key.slice(4));

      const base = enabledCats.length > 0 ? enabledCats : [...ALL_LEAD_CATEGORIES];
      setAllowedCategories(
        base.filter(c => (BROKERAGE_CATEGORIES.includes(c) ? !!brokerage : true))
      );
    } catch (error) {
      console.error('Error checking category access:', error);
      setAllowedCategories([]);
    }
  };


  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check roles after state update
        if (session?.user) {
          checkRoles(session.user.id);
        } else {
          setIsAdmin(false);
          setIsBroker(false);
          setCanAccessBrokerage(false);
          setAllowedCategories([]);
          setRolesLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkRoles(session.user.id);
      } else {
        setRolesLoading(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin'
      });
      
      if (error) throw error;
      setIsAdmin(data || false);
    } catch (error) {
      console.error('Error checking admin role:', error);
      setIsAdmin(false);
    }
  };

  const checkBrokerRole = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'mäklare'
      });
      
      if (error) throw error;
      setIsBroker(data || false);
    } catch (error) {
      console.error('Error checking broker role:', error);
      setIsBroker(false);
    }
  };

  // Separate from role "mäklare": admin OR permission can_access_brokerage (evaluated in DB)
  const checkBrokeragePermission = async (_userId: string) => {
    const { data, error } = await supabase.rpc('can_access_brokerage');
    if (error) console.error('Error checking brokerage permission:', error);
    setCanAccessBrokerage(!error && !!data);
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      // Wait briefly for role to be checked before redirecting
      setTimeout(() => {
        toast.success('Inloggad!');
        // Will be redirected based on role in Auth page
      }, 500);
    } catch (error: any) {
      toast.error(error.message || 'Inloggningen misslyckades');
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          }
        }
      });
      
      if (error) throw error;
      
      toast.success('Konto skapat! Du kan nu logga in.');
    } catch (error: any) {
      if (error.message.includes('already registered')) {
        toast.error('Den här e-postadressen är redan registrerad');
      } else {
        toast.error(error.message || 'Registreringen misslyckades');
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setIsAdmin(false);
      setIsBroker(false);
      setCanAccessBrokerage(false);
      setAllowedCategories([]);
      toast.success('Utloggad');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'Utloggningen misslyckades');
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, rolesLoading, signIn, signUp, signOut, isAdmin, isBroker, canAccessBrokerage, allowedCategories }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
